import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { DesignHuddleAssetPreviewModal } from "./design-huddle-asset-preview-modal";
import type { Asset } from "./server/digital-asset-service";
import { UploadedFileAssetPreviewModal } from "./uploaded-file-asset-preview-modal";

export const AssetPreviewModal: FC<{
  open: boolean;
  onClose: () => void;
  asset: Asset | null;
  onChange: () => void;
  onArchived?: () => void;
  onViewFileClick: (asset: Asset) => void;
  onFolderChange: () => void;
  folders_feature_flag?: boolean;
}> = ({
  asset,
  open,
  onClose,
  onChange,
  onArchived,
  onViewFileClick,
  onFolderChange,
  folders_feature_flag = false,
}) => {
  if (!asset) return null;

  return asset.content_type === "Uploaded File" ? (
    <UploadedFileAssetPreviewModal
      asset={asset}
      open={open}
      onClose={onClose}
      onChange={onChange}
      onArchived={onArchived}
      onViewFileClick={onViewFileClick}
      onFolderChange={onFolderChange}
      folders_feature_flag={folders_feature_flag}
    />
  ) : (
    <DesignHuddleAssetPreviewModal
      asset={asset}
      open={open}
      onClose={onClose}
      onChange={onChange}
      onFolderChange={onFolderChange}
      onArchived={onArchived}
      folders_feature_flag={folders_feature_flag}
    />
  );
};

export const ArchiveConfirmation: FC<{
  asset?: Asset;
  assetCount?: number;
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ asset, assetCount = 0, open, onClose, onConfirm }) => {
  const count = asset ? 1 : Math.max(assetCount, 1);
  const noun = count === 1 ? "asset" : "assets";
  const thisOrThese = count === 1 ? "this asset" : "these assets";
  const itOrThem = count === 1 ? "it" : "them";
  const title = asset ? `Archive '${asset.title}'?` : `Archive ${count} ${noun}?`;
  const confirmLabel = asset ? "Archive" : `Archive ${noun}`;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography sx={{ pb: 3 }}>
          {`Archiving ${thisOrThese} will hide ${itOrThem} from your Content Library. Embedded links to ${thisOrThese} will continue to work, but no one will be able to view ${itOrThem} in the Content Library.`}
        </Typography>
        <Typography sx={{ pb: 3 }}>
          {`Alternatively, you can change or edit ${thisOrThese} to replace ${itOrThem}.`}
        </Typography>
        <Typography>{`If you want to permanently delete ${thisOrThese}, contact support@changeengine.com.`}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} data-analytics-id="asset-delete-cancel" variant="text">
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          color="error"
          data-analytics-id="asset-archive-confirm"
          variant="contained"
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
